import { useState, useEffect, useCallback } from 'react';
import {
  hasConsent,
  getConsentStatus,
  saveConsent as saveCookieConsent,
  loadApolloScript,
  loadGoogleAnalyticsScript,
  loadClarityScript,
} from '@/utils/apolloTracking';

/**
 * Custom hook for managing cookie consent state
 * Provides consent status and functions to accept/decline tracking
 */
export function useCookieConsent() {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [consentStatus, setConsentStatus] = useState<'accepted' | 'declined' | null>(null);

  // Check consent status on mount
  useEffect(() => {
    const status = getConsentStatus();
    setConsentStatus(status);
    setShowBanner(!hasConsent());

    // If consent already accepted, load all tracking scripts
    if (status === 'accepted') {
      loadApolloScript();
      loadGoogleAnalyticsScript();
      loadClarityScript();
    }
  }, []);

  // Accept tracking consent
  const acceptConsent = useCallback(() => {
    saveCookieConsent('accepted');
    setConsentStatus('accepted');
    setShowBanner(false);
    
    // Load all tracking scripts
    loadApolloScript();
    loadGoogleAnalyticsScript();
    loadClarityScript();
  }, []);

  // Decline tracking consent
  const declineConsent = useCallback(() => {
    saveCookieConsent('declined');
    setConsentStatus('declined');
    setShowBanner(false);
  }, []);

  return {
    showBanner,
    consentStatus,
    acceptConsent,
    declineConsent,
  };
}
